import { authService } from './auth';

export interface CloudbridgeUploadResult {
  url: string;
  shortUrl?: string;
  filename?: string;
  nextcloudPath?: string;
}

const DEFAULT_COMPOSE_FOLDER = 'mail-attachments/compose-images';
const DEFAULT_SIGNATURE_FOLDER = 'mail-attachments/signature-images';

async function authFetch(url: string, options: RequestInit = {}) {
  const token = authService.getAccessToken();
  const response = await fetch(`${authService.API_BASE_URL}${url}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.message || data.error || `Request failed (${response.status})`);
  }
  return data;
}

export async function uploadCloudbridgeImage(
  file: File,
  folder = DEFAULT_COMPOSE_FOLDER,
  onProgress?: (percent: number) => void,
): Promise<CloudbridgeUploadResult> {
  const token = authService.getAccessToken();
  const url = `${authService.API_BASE_URL}/api/upload/cloudbridge`;

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', folder);

    xhr.upload.addEventListener('progress', (event) => {
      if (!onProgress) return;
      if (event.lengthComputable) {
        const percent = Math.min(100, Math.round((event.loaded / event.total) * 100));
        onProgress(percent);
        return;
      }
      onProgress(0);
    });

    xhr.addEventListener('load', () => {
      let data: Record<string, any> = {};
      try {
        data = xhr.responseText ? JSON.parse(xhr.responseText) : {};
      } catch {
        reject(new Error(`Upload failed (${xhr.status})`));
        return;
      }

      if (xhr.status < 200 || xhr.status >= 300) {
        reject(new Error(data.message || data.error || `Upload failed (${xhr.status})`));
        return;
      }

      if (!data.url) {
        reject(new Error('CloudBridge upload succeeded but no URL was returned'));
        return;
      }

      onProgress?.(100);
      resolve({
        url: data.url,
        shortUrl: data.shortUrl,
        filename: data.filename,
        nextcloudPath: data.nextcloudPath,
      });
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Network error while uploading image'));
    });

    xhr.addEventListener('abort', () => {
      reject(new Error('Image upload cancelled'));
    });

    xhr.open('POST', url);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.send(formData);
  });
}

export async function deleteCloudbridgeImage(path: string): Promise<void> {
  if (!path?.trim()) return;

  await authFetch('/api/upload/cloudbridge/delete', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ path }),
  });
}

export const cloudbridgeFolders = {
  compose: DEFAULT_COMPOSE_FOLDER,
  signature: DEFAULT_SIGNATURE_FOLDER,
};

export function extractImageSrcs(html: string): Set<string> {
  const srcs = new Set<string>();
  if (!html) return srcs;

  const regex = /src\s*=\s*(["'])(.*?)\1/gi;
  let match = regex.exec(html);
  while (match) {
    const src = match[2]?.trim();
    if (src) srcs.add(src);
    match = regex.exec(html);
  }

  return srcs;
}
